import { useEffect, useState } from "react"

import {
  useLazyGetIntelligenceReportingPeriodQuery,
  useLazyGetIntelligenceReportsQuery,
} from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { ContentWrapper } from "components/common/ContentWrapper"

import { IntelligenceReportDrawer, type IntelligenceReportDrawerRow } from "./components/IntelligenceReportDrawer"
import { IntelligenceReportsEmptyState } from "./components/IntelligenceReportsEmptyState"
import { IntelligenceReportsHeader } from "./components/IntelligenceReportsHeader"
import { IntelligenceReportsTable } from "./components/IntelligenceReportsTable"
import { convertIntelligenceReportsToTableRows } from "./convertIntelligenceReportsToTableRows"
import IntelligenceReportsInfoBanner from "./IntelligenceReportsInfoBanner"
import intelligenceReportsTableHeaderCells from "./intelligenceReportsTableHeaderCells"

const PER_PAGE = 15

const formatDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split("-")
  return `${month}.${day}.${year}`
}

const IntelligenceReportsPage = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasIntelligenceReportsAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.IntelligenceReports)
    : false
  const [drawerReport, setDrawerReport] = useState<IntelligenceReportDrawerRow | null>(null)
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const activePriorityTab = (urlQueryParams.priority as string) || "high"
  const currentPage = parseInt((urlQueryParams.page as string) || "1", 10)
  const orderColumn = (urlQueryParams.orderColumn as string) || null
  const orderType = (urlQueryParams.orderType as string) || null

  const [fetchReportingPeriod, { data: periodData }] = useLazyGetIntelligenceReportingPeriodQuery()
  const [fetchReports, { data: reportsData, isFetching, isUninitialized }] = useLazyGetIntelligenceReportsQuery()

  useEffect(() => {
    fetchReportingPeriod()
  }, [])

  useEffect(() => {
    if (!periodData) return

    const params: UITypes.Params = {
      start_date: periodData.data.start_date,
      end_date: periodData.data.end_date,
      page: currentPage,
      pageSize: PER_PAGE,
    }

    if (urlQueryParams.sn) params.sn = urlQueryParams.sn as string
    if (urlQueryParams.customer_name) params.customer_name = urlQueryParams.customer_name as string
    if (urlQueryParams.customer_email) params.customer_email = urlQueryParams.customer_email as string
    if (activePriorityTab) params.priority = activePriorityTab
    if (orderColumn) params.orderColumn = orderColumn
    if (orderType) params.orderType = orderType as UITypes.Params["orderType"]

    fetchReports(params)
  }, [periodData, currentPage, urlQueryParams, activePriorityTab, orderColumn, orderType])

  const reportingPeriodLabel = periodData
    ? `${formatDate(periodData.data.start_date)} - ${formatDate(periodData.data.end_date)}`
    : null

  const tableData = reportsData ? convertIntelligenceReportsToTableRows(reportsData.data) : []
  const meta = reportsData?.meta ?? {
    total: 0,
    per_page: PER_PAGE,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
  }

  const handlePriorityTabClick = (priority: string) => {
    if (priority === "") {
      setUrlQueryParams({ priority: null, page: 1 })
    } else {
      setUrlQueryParams({ priority, page: 1 })
    }
  }

  const hasSearchFilters = !!(urlQueryParams.sn || urlQueryParams.customer_name || urlQueryParams.customer_email)
  const showEmptyState = !isUninitialized && !isFetching && tableData.length === 0 && !hasSearchFilters

  const { handleLocalExport } = useExportCSV({
    params: {},
    headerCells: intelligenceReportsTableHeaderCells,
    fileName: "intelligence_reports",
  })

  return (
    <>
      <IntelligenceReportDrawer report={drawerReport} onClose={() => setDrawerReport(null)} />
      {!hasIntelligenceReportsAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.IntelligenceReports} />}
      <IntelligenceReportsInfoBanner />
      <ContentWrapper title="Reports">
        <IntelligenceReportsHeader
          onExport={() => handleLocalExport(tableData)}
          isExportDisabled={meta.total === 0}
          reportingPeriodLabel={reportingPeriodLabel}
          activePriorityTab={activePriorityTab}
          onPriorityTabClick={handlePriorityTabClick}
        />
        {showEmptyState ? (
          <IntelligenceReportsEmptyState priority={activePriorityTab} />
        ) : (
          <IntelligenceReportsTable
            data={tableData}
            isLoading={isFetching}
            meta={meta}
            onViewReport={setDrawerReport}
          />
        )}
      </ContentWrapper>
    </>
  )
}

export default IntelligenceReportsPage
